import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Description as WordIcon } from '@mui/icons-material';
import { filter, get, startCase } from 'lodash';
import { observer } from 'mobx-react-lite';

// import { exportToPDF } from 'utils/export/pdfExport';
import { exportToWord } from 'utils/export/wordExport';
import { UnderwritingOutput } from 'types/underwriting/underwriting';
import { ProFormaTable } from '../income-approach/pro-forma/ProFormaTable';
import AdjustmentsAndLossesDisabledTableView from '../income-approach/cap-rate-conclusion/table/AdjustmentsAndLossesDisabledTableView';
import { getExpenseHistoryData, getFinancingTermsData, getPropertyData } from './helpers';
import { formatCurrencyInt, formatFloat, formatInt, formatPercentageString } from 'shared/utils/numberFormatters';
import ComparableExpensesTable from '../income-approach/expenses/comparable-expenses/ComparableExpensesTable';
import { DEFAULT_EXPENSES_KEYS, EXPENSE_COMPARABLES_INFO } from 'shared/constants/expenses';
import { incomeApproachCalculations } from 'shared/calculations/incomeApproach';
import { EXPENSE_RATIO_INPUT_METHOD, EXPENSE_SUMMARIZED_ENTRY_MODE } from 'shared/constants/expenses';
import { EntryMode } from 'types/underwriting/income-approach/expenses';
import { ExpenseCategory } from '../income-approach/expenses/types';

interface DocumentSection {
  id: string;
  title: string;
  enabled: boolean;
  beforeText: string;
  afterText: string;
  data: any;
}

interface DocumentEditorProps {
  underwritingData: UnderwritingOutput;
  form: any;
}

const DocumentEditor: React.FC<DocumentEditorProps> = observer(({ underwritingData, form }) => {
  const { property, incomeApproach } = underwritingData;
  const propertyData = getPropertyData(property);
  const financingTermsData = getFinancingTermsData(underwritingData);

  const expenseHistoryEntryMode: EntryMode | undefined = get(
    underwritingData,
    'incomeApproach.expenses.expenseHistoryEntryMode',
    'detailed'
  );

  const getSections = (): DocumentSection[] => {
    const sections = [
      {
        id: 'propertyDetails',
        title: 'Property Details',
        enabled: form.$('export.sections.propertyDetails.enabled').value,
        beforeText: form.$('export.sections.propertyDetails.beforeText').value,
        afterText: form.$('export.sections.propertyDetails.afterText').value,
        data: propertyData,
      },
      {
        id: 'residentialRentRoll',
        title: 'Residential Income',
        enabled: form.$('export.sections.residentialRentRoll.enabled').value,
        beforeText: form.$('export.sections.residentialRentRoll.beforeText').value,
        afterText: form.$('export.sections.residentialRentRoll.afterText').value,
        data: incomeApproach?.residential?.rentRoll || [],
      },
      {
        id: 'residentialRentComps',
        title: 'Residential Rent Comparables',
        enabled: form.$('export.sections.residentialRentComps.enabled').value,
        beforeText: form.$('export.sections.residentialRentComps.beforeText').value,
        afterText: form.$('export.sections.residentialRentComps.afterText').value,
        data: incomeApproach?.residential?.rentComps?.comps || [],
      },
      {
        id: 'commercialRentRoll',
        title: 'Commercial Income',
        enabled: form.$('export.sections.commercialRentRoll.enabled').value,
        beforeText: form.$('export.sections.commercialRentRoll.beforeText').value,
        afterText: form.$('export.sections.commercialRentRoll.afterText').value,
        data: incomeApproach?.commercial?.rentRoll || [],
      },
      {
        id: 'commercialRentComps',
        title: 'Commercial Rent Comparables',
        enabled: form.$('export.sections.commercialRentComps.enabled').value,
        beforeText: form.$('export.sections.commercialRentComps.beforeText').value,
        afterText: form.$('export.sections.commercialRentComps.afterText').value,
        data: incomeApproach?.commercial?.rentComps?.comps || [],
      },
      {
        id: 'expenseHistory',
        title: 'Expense History',
        enabled: form.$('export.sections.expenseHistory.enabled').value,
        beforeText: form.$('export.sections.expenseHistory.beforeText').value,
        afterText: form.$('export.sections.expenseHistory.afterText').value,
        data: {
          expenseHistory: incomeApproach?.expenses?.expenseHistory || [],
          expenseCategories: incomeApproach?.expenses?.expenseCategories || [],
          expenseHistoryEntryMode,
          expenseHistoryInputMethod: incomeApproach?.expenses?.expenseHistoryInputMethod || 'amount',
          effectiveGrossIncome: incomeApproachCalculations.calculateEffectiveGrossIncome(underwritingData) || 0,
        },
      },
      {
        id: 'expenseComps',
        title: 'Expense Comps',
        enabled: form.$('export.sections.expenseComps.enabled').value,
        beforeText: form.$('export.sections.expenseComps.beforeText').value,
        afterText: form.$('export.sections.expenseComps.afterText').value,
        data: {
          expenseComps: incomeApproach?.expenses?.expenseComps || [],
          expenseCategories:
            expenseHistoryEntryMode !== EXPENSE_SUMMARIZED_ENTRY_MODE
              ? incomeApproach?.expenses?.expenseCategories || []
              : filter(incomeApproach?.expenses?.expenseCategories || [], (category: ExpenseCategory) =>
                  Object.values(DEFAULT_EXPENSES_KEYS).includes(category.key)
                ),
        },
      },
      {
        id: 'proForma',
        title: 'Pro Forma',
        enabled: form.$('export.sections.proForma.enabled').value,
        beforeText: form.$('export.sections.proForma.beforeText').value,
        afterText: form.$('export.sections.proForma.afterText').value,
        data: {
          underwritingData,
        },
      },
      {
        id: 'capRateConclusion',
        title: 'Cap Rate Conclusion',
        enabled: form.$('export.sections.capRateConclusion.enabled').value,
        beforeText: form.$('export.sections.capRateConclusion.beforeText').value,
        afterText: form.$('export.sections.capRateConclusion.afterText').value,
        data: underwritingData,
      },
      {
        id: 'financingTerms',
        title: 'Financing Terms',
        enabled: form.$('export.sections.financingTerms.enabled').value,
        beforeText: form.$('export.sections.financingTerms.beforeText').value,
        afterText: form.$('export.sections.financingTerms.afterText').value,
        data: financingTermsData,
      },
    ];

    return sections;
  };

  const renderPropertyTable = (data: any, excludeEmpty: boolean = true) => {
    const entries = Object.entries(data).filter(([key, value]) => {
      if (excludeEmpty && (value === undefined || value === '' || value === null)) {
        return false;
      }
      return true;
    });

    if (entries.length === 0) return null;

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Field</strong>
              </TableCell>
              <TableCell>
                <strong>Value</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(([key, value]) => (
              <TableRow key={key}>
                <TableCell>{key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}</TableCell>
                <TableCell>{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderResidentialRentRollTable = (rentRoll: any[]) => {
    if (!rentRoll || rentRoll.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No residential units available
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Unit #</strong>
              </TableCell>
              <TableCell>
                <strong>Bedrooms</strong>
              </TableCell>
              <TableCell>
                <strong>Bathrooms</strong>
              </TableCell>
              <TableCell>
                <strong>Square Footage</strong>
              </TableCell>
              <TableCell>
                <strong>Current Rent</strong>
              </TableCell>
              <TableCell>
                <strong>Underwriting</strong>
              </TableCell>
              <TableCell>
                <strong>Lease Status</strong>
              </TableCell>
              <TableCell>
                <strong>Rent Type</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rentRoll.map((unit, index) => (
              <TableRow key={unit.id || index}>
                <TableCell>{unit.number || `Unit ${index + 1}`}</TableCell>
                <TableCell>{unit.bedrooms || 'N/A'}</TableCell>
                <TableCell>{unit.bathrooms || 'N/A'}</TableCell>
                <TableCell>{unit.squareFootage ? formatInt(unit.squareFootage) : 'N/A'}</TableCell>
                <TableCell>{unit.rent ? formatCurrencyInt(unit.rent) : 'N/A'}</TableCell>
                <TableCell>{unit.projectedRent ? formatCurrencyInt(unit.projectedRent) : 'N/A'}</TableCell>
                <TableCell>{unit.leaseStatus || 'N/A'}</TableCell>
                <TableCell>{unit.rentType || 'N/A'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderResidentialRentCompsTable = (rentComps: any[]) => {
    if (!rentComps || rentComps.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No residential rent comparables available
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Address</strong>
              </TableCell>
              <TableCell>
                <strong>Unit #</strong>
              </TableCell>
              <TableCell>
                <strong>Bedrooms</strong>
              </TableCell>
              <TableCell>
                <strong>Bathrooms</strong>
              </TableCell>
              <TableCell>
                <strong>Sq. Ft.</strong>
              </TableCell>
              <TableCell>
                <strong>Rent</strong>
              </TableCell>
              <TableCell>
                <strong>Rent PSF</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rentComps.map((comp, index) => (
              <TableRow key={comp.id || index}>
                <TableCell>{comp.address || 'N/A'}</TableCell>
                <TableCell>{comp.unitNumber || 'N/A'}</TableCell>
                <TableCell>{comp.bedrooms}</TableCell>
                <TableCell>{comp.bathrooms || 'N/A'}</TableCell>
                <TableCell>{comp.squareFeet ? formatInt(comp.squareFeet) : 'N/A'}</TableCell>
                <TableCell>{comp.monthlyRent ? formatCurrencyInt(comp.monthlyRent) : 'N/A'}</TableCell>
                <TableCell>{comp.monthlyRentPerSF ? formatCurrencyInt(comp.monthlyRentPerSF) : 'N/A'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderCommercialRentRollTable = (rentRoll: any[]) => {
    if (!rentRoll || rentRoll.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No commercial units available
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Unit #</strong>
              </TableCell>
              <TableCell>
                <strong>Use</strong>
              </TableCell>
              <TableCell>
                <strong>Tenant</strong>
              </TableCell>
              <TableCell>
                <strong>Square Footage</strong>
              </TableCell>
              <TableCell>
                <strong>Current Rent</strong>
              </TableCell>
              <TableCell>
                <strong>Underwriting</strong>
              </TableCell>
              <TableCell>
                <strong>Lease Status</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rentRoll.map((unit, index) => (
              <TableRow key={unit.id || index}>
                <TableCell>{unit.number || `Unit ${index + 1}`}</TableCell>
                <TableCell>{unit.use || 'N/A'}</TableCell>
                <TableCell>{unit.tenant || 'N/A'}</TableCell>
                <TableCell>{unit.squareFootage ? formatInt(unit.squareFootage) : 'N/A'}</TableCell>
                <TableCell>{unit.rent ? formatCurrencyInt(unit.rent) : 'N/A'}</TableCell>
                <TableCell>{unit.projectedRent ? formatCurrencyInt(unit.projectedRent) : 'N/A'}</TableCell>
                <TableCell>{unit.leaseStatus || 'N/A'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderCommercialRentCompsTable = (rentComps: any[]) => {
    if (!rentComps || rentComps.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No commercial rent comparables available
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Address</strong>
              </TableCell>
              <TableCell>
                <strong>Use</strong>
              </TableCell>
              <TableCell>
                <strong>Tenant Name</strong>
              </TableCell>
              <TableCell>
                <strong>Sq. Ft.</strong>
              </TableCell>
              <TableCell>
                <strong>Rent</strong>
              </TableCell>
              <TableCell>
                <strong>Annual Rent PSF</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rentComps.map((comp, index) => (
              <TableRow key={comp.id || index}>
                <TableCell>{comp.address || 'N/A'}</TableCell>
                <TableCell>{comp.use}</TableCell>
                <TableCell>{comp.tenantName || 'N/A'}</TableCell>
                <TableCell>{comp.squareFeet ? formatInt(comp.squareFeet) : 'N/A'}</TableCell>
                <TableCell>{comp.monthlyRent ? formatCurrencyInt(comp.monthlyRent) : 'N/A'}</TableCell>
                <TableCell>{comp.annualRentPerSF ? formatCurrencyInt(comp.annualRentPerSF) : 'N/A'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderExpenseHistoryTable = (data: any) => {
    const {
      expenseHistory,
      expenseCategories,
      expenseHistoryEntryMode,
      expenseHistoryInputMethod,
      effectiveGrossIncome,
    } = data;
    if (!expenseHistory || expenseHistory.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No expense history available
        </Typography>
      );
    }
    const { yearColumns, rows, totalRow, totalExcludingRETaxesRow } = getExpenseHistoryData(
      expenseHistory,
      expenseCategories,
      expenseHistoryEntryMode,
      expenseHistoryInputMethod,
      effectiveGrossIncome
    );

    const dataRowFormatter =
      expenseHistoryEntryMode === EXPENSE_SUMMARIZED_ENTRY_MODE
        ? expenseHistoryInputMethod === EXPENSE_RATIO_INPUT_METHOD
          ? formatPercentageString
          : formatCurrencyInt
        : formatCurrencyInt;

    const totalRowFormatter =
      expenseHistoryEntryMode === EXPENSE_SUMMARIZED_ENTRY_MODE
        ? expenseHistoryInputMethod === EXPENSE_RATIO_INPUT_METHOD
          ? formatCurrencyInt
          : formatPercentageString
        : formatCurrencyInt;

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Category</strong>
              </TableCell>
              {yearColumns.map((year) => (
                <TableCell key={year}>
                  <strong>{startCase(year)}</strong>
                </TableCell>
              ))}
              <TableCell>
                <strong>Average</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: any, index: number) => (
              <TableRow key={row.id || index}>
                <TableCell>{row.category || 'N/A'}</TableCell>
                {yearColumns.map((year) => (
                  <TableCell key={year}>
                    {row[year] && typeof row[year] === 'number' ? dataRowFormatter(row[year]) : 'N/A'}
                  </TableCell>
                ))}
                <TableCell>
                  {row.average && typeof row.average === 'number' ? dataRowFormatter(row.average) : 'N/A'}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell style={{ fontWeight: 'bold' }}>{totalRow.category || 'N/A'}</TableCell>
              {yearColumns.map((year) => (
                <TableCell key={year} style={{ fontWeight: 'bold' }}>
                  {totalRow[year] && typeof totalRow[year] === 'number' ? totalRowFormatter(totalRow[year]) : 'N/A'}
                </TableCell>
              ))}
              <TableCell style={{ fontWeight: 'bold' }}>
                {totalRow.average && typeof totalRow.average === 'number' ? totalRowFormatter(totalRow.average) : 'N/A'}
              </TableCell>
            </TableRow>
            {totalExcludingRETaxesRow && (
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }}>{totalExcludingRETaxesRow.category || 'N/A'}</TableCell>
                {yearColumns.map((year) => (
                  <TableCell key={year} style={{ fontWeight: 'bold' }}>
                    {totalExcludingRETaxesRow[year] && typeof totalExcludingRETaxesRow[year] === 'number'
                      ? formatCurrencyInt(totalExcludingRETaxesRow[year])
                      : 'N/A'}
                  </TableCell>
                ))}
                <TableCell style={{ fontWeight: 'bold' }}>
                  {totalExcludingRETaxesRow.average && typeof totalExcludingRETaxesRow.average === 'number'
                    ? formatCurrencyInt(totalExcludingRETaxesRow.average)
                    : 'N/A'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderExpenseCompsTable = (data: any) => {
    const { expenseComps, expenseCategories } = data;
    const expenseInfoCategories = EXPENSE_COMPARABLES_INFO.map((infoKey) => {
      return {
        key: infoKey,
        category: startCase(infoKey),
      };
    });
    const allTableCategories = [...expenseInfoCategories, ...expenseCategories];

    if (!expenseComps || expenseComps.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No expense comparables available
        </Typography>
      );
    }

    return (
      <ComparableExpensesTable expenseComparables={expenseComps} expenseCategories={allTableCategories} disabled />
    );
  };

  const renderProFormaTable = () => {
    return <ProFormaTable underwritingData={underwritingData} />;
  };

  const renderCapRateConclusionTable = (underwritingData: UnderwritingOutput) => {
    if (!underwritingData) {
      return (
        <Typography variant="body2" color="text.secondary">
          No cap rate conclusion data available
        </Typography>
      );
    }
    return <AdjustmentsAndLossesDisabledTableView underwritingData={underwritingData} />;
  };

  const renderFinancingTermsTable = (financingData: any[]) => {
    if (!financingData || financingData.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No financing terms data available
        </Typography>
      );
    }

    const formatFinancingValue = (item: string, value: any): string => {
      if (value === undefined || value === null) return 'N/A';

      const itemLower = item.toLowerCase();
      if (itemLower.includes('rate') || itemLower.includes('ltv')) {
        return formatPercentageString(value) || 'N/A';
      }
      if (itemLower.includes('income') || itemLower.includes('value') || itemLower.includes('amount')) {
        return formatCurrencyInt(value) || 'N/A';
      }
      if (itemLower.includes('term')) {
        return `${formatInt(value)} years`;
      }
      if (itemLower.includes('ratio')) {
        return formatFloat(value, 4) || 'N/A';
      } else if (itemLower.includes('debt')) {
        return formatCurrencyInt(value) || 'N/A';
      }
      return String(value);
    };

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Item</strong>
              </TableCell>
              <TableCell>
                <strong>Borrower</strong>
              </TableCell>
              <TableCell>
                <strong>Underwriter</strong>
              </TableCell>
              <TableCell>
                <strong>Hypothetical</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {financingData.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{item.item || 'N/A'}</TableCell>
                <TableCell>{formatFinancingValue(item.item, item.borrower)}</TableCell>
                <TableCell>{formatFinancingValue(item.item, item.underwriter)}</TableCell>
                <TableCell>{formatFinancingValue(item.item, item.hypothetical)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // const handleExportPDF = () => {
  //   const propertyAddress = property.address;
  //   const documentTitle = `Property Report - ${propertyAddress?.street || 'Unknown Address'}`;

  //   exportToPDF({
  //     title: documentTitle,
  //     sections: getSections(),
  //     author: 'Bowery Valuation',
  //     subject: 'Property Information Report',
  //   });
  // };

  const handleExportWord = async () => {
    const propertyAddress = property.address;
    const documentTitle = `${propertyAddress?.street || 'Unknown Address'}`;

    await exportToWord({
      title: documentTitle,
      sections: getSections(),
      author: 'Bowery Valuation',
      subject: 'Property Information Report',
    });
  };

  const sections = getSections();
  const enabledSections = sections.filter((section) => section.enabled);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Document Export Editor</Typography>
        <Stack direction="row" spacing={2}>
          {/* <Button
            variant="contained"
            startIcon={<PdfIcon />}
            onClick={handleExportPDF}
            disabled={enabledSections.length === 0}
          >
            Export PDF
          </Button> */}
          <Button
            variant="contained"
            startIcon={<WordIcon />}
            onClick={handleExportWord}
            disabled={enabledSections.length === 0}
          >
            Export Word Document
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure which property sections to include in your export document and add custom text before and after each
        section.
      </Typography>

      <Stack spacing={2}>
        {sections.map((section) => (
          <Accordion key={section.id} defaultExpanded={section.enabled}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControlLabel
                  control={<Checkbox {...form.$(`export.sections.${section.id}.enabled`).bind()} />}
                  label={section.title}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={3}>
                <TextField
                  {...form.$(`export.sections.${section.id}.beforeText`).bind()}
                  label="Text before section"
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Add introductory text that will appear before this section..."
                />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Preview:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {section.id === 'residentialRentRoll'
                      ? renderResidentialRentRollTable(section.data)
                      : section.id === 'residentialRentComps'
                      ? renderResidentialRentCompsTable(section.data)
                      : section.id === 'commercialRentRoll'
                      ? renderCommercialRentRollTable(section.data)
                      : section.id === 'commercialRentComps'
                      ? renderCommercialRentCompsTable(section.data)
                      : section.id === 'expenseHistory'
                      ? renderExpenseHistoryTable(section.data)
                      : section.id === 'expenseComps'
                      ? renderExpenseCompsTable(section.data)
                      : section.id === 'proForma'
                      ? renderProFormaTable()
                      : section.id === 'capRateConclusion'
                      ? renderCapRateConclusionTable(section.data)
                      : section.id === 'financingTerms'
                      ? renderFinancingTermsTable(section.data)
                      : renderPropertyTable(section.data)}
                  </Paper>
                </Box>
                <TextField
                  {...form.$(`export.sections.${section.id}.afterText`).bind()}
                  label="Text after section"
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Add concluding text that will appear after this section..."
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      {enabledSections.length === 0 && (
        <Paper sx={{ p: 3, mt: 2, backgroundColor: 'warning.light' }}>
          <Typography variant="body2">
            No sections are enabled for export. Please select at least one section to enable export.
          </Typography>
        </Paper>
      )}
    </Box>
  );
});

export default DocumentEditor;
